/**
 * API client for scheduler-worker.
 * In development, uses X-Mock-User-Role header for auth.
 * In production, Cloudflare Access JWT handles auth.
 */

import type { Task, TaskStatus, LaborBucket } from 'scheduler-types';

// API base URL
// - Uses VITE_API_URL if set
// - Falls back to localhost:8787 in development
// - Falls back to same-origin (empty string) in production
const API_BASE_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? 'http://localhost:8787' : '');

const API_BASE = `${API_BASE_URL}/api/v1`;

// Response types based on API-SPEC.md
export interface MyTasksResponse {
  data: WorkerTask[];
  activeTask: {
    id: string;
    startedAt: string;
  } | null;
}

/**
 * Response type for task detail (from GET /api/v1/tasks/:id)
 */
export interface TaskDetailResponse {
  data: {
    id: string;
    lineItemId: string;
    bomId: string;
    partNumber: string;
    laborBucket: LaborBucket;
    estimatedMinutesMin: number;
    estimatedMinutesMax: number;
    actualMinutes: number;
    requiredSkill: {
      id: string;
      name: string;
    };
    assignedPerson: {
      id: string;
      name: string;
    } | null;
    isPinned: boolean;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    status: TaskStatus;
    dependencies: Array<{
      taskId: string;
      dependencyType: string;
      status: TaskStatus;
    }>;
    blockedBy: {
      type: 'parts' | 'dependency' | 'eco_gate' | 'skill';
      details: string;
    } | null;
  };
}

export interface WorkerTask {
  id: string;
  partNumber: string;
  description: string;
  laborBucket: LaborBucket;
  projectName: string; // For employees; contractors see gpSalesOrderNumber
  gpSalesOrderNumber?: string; // For contractors
  priority: number;
  estimatedMinutes: number;
  actualMinutes: number;
  estimatedMinutesMin: number;
  estimatedMinutesMax: number;
  status: TaskStatus;
  scheduledStart: string | null;
}

/**
 * Make an API request with proper headers.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    // Mock auth headers for development and staging (CF_ENV=development on server)
    'X-Mock-User-Role': 'worker',
    'X-Mock-User-Email': 'chammock@abetech.com',
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // For Cloudflare Access cookies in production
  });

  if (!response.ok) {
    // Read response text first, then try to parse as JSON
    const responseText = await response.text();
    let errorCode = 'UNKNOWN_ERROR';
    let errorMessage = `API error: ${response.status}`;

    try {
      const error = JSON.parse(responseText);
      errorCode = error.error?.code ?? errorCode;
      errorMessage = error.error?.message ?? errorMessage;
    } catch {
      // JSON parse failed - use response text if available, otherwise statusText
      errorMessage = responseText || response.statusText || errorMessage;
    }

    throw new ApiError(errorCode, errorMessage, response.status);
  }

  return response.json();
}

/**
 * Custom error class for API errors.
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Fetch tasks assigned to the current user.
 */
export async function fetchMyTasks(): Promise<MyTasksResponse> {
  return apiRequest<MyTasksResponse>('/me/tasks');
}

/**
 * Fetch a single task by ID.
 */
export async function fetchTask(taskId: string): Promise<TaskDetailResponse> {
  return apiRequest<TaskDetailResponse>(`/tasks/${taskId}`);
}

/**
 * Start working on a task.
 */
export async function startTask(
  taskId: string,
  deviceTimestamp: string
): Promise<{ data: Task }> {
  return apiRequest<{ data: Task }>(`/tasks/${taskId}/start`, {
    method: 'POST',
    body: JSON.stringify({ deviceTimestamp }),
  });
}

/**
 * Stop working on a task (pause).
 */
export async function stopTask(
  taskId: string,
  deviceTimestamp: string
): Promise<{ data: Task }> {
  return apiRequest<{ data: Task }>(`/tasks/${taskId}/stop`, {
    method: 'POST',
    body: JSON.stringify({ deviceTimestamp }),
  });
}

/**
 * Complete a task.
 */
export async function completeTask(
  taskId: string,
  deviceTimestamp: string
): Promise<{ data: Task }> {
  return apiRequest<{ data: Task }>(`/tasks/${taskId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ deviceTimestamp }),
  });
}

/**
 * Start a serialized task - assigns serial number.
 * REQUIRES CONNECTIVITY - cannot be done offline.
 */
export interface StartSerializedResponse {
  serialNumber: string;
  serializedUnitId: string;
}

export async function startSerializedTask(taskId: string): Promise<StartSerializedResponse> {
  return apiRequest<StartSerializedResponse>(`/tasks/${taskId}/start-serialized`, {
    method: 'POST',
  });
}

// --- Sync Time Entries ---

export interface SyncTimeEntriesRequest {
  entries: Array<{
    clientEntryId: string;
    taskId: string;
    startTime: string;
    endTime: string | null;
    deviceTimeOffsetSeconds: number;
  }>;
}

export interface SyncTimeEntriesResponse {
  data: {
    results: Array<{
      clientEntryId: string;
      status: 'created' | 'duplicate' | 'conflict';
      timeEntryId?: string;
    }>;
    summary: {
      created: number;
      duplicate: number;
      conflict: number;
    };
  };
}

/**
 * Sync offline time entries to the server.
 * Uses clientEntryId for idempotency.
 */
export async function syncTimeEntries(
  request: SyncTimeEntriesRequest
): Promise<SyncTimeEntriesResponse> {
  return apiRequest<SyncTimeEntriesResponse>('/time-entries/sync', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// --- Time Entry History ---

export interface TimeEntryRecord {
  id: string;
  clientEntryId: string;
  taskId: string;
  startTime: string;
  endTime: string | null;
  durationMinutes: number | null;
  syncStatus: string;
  resolutionStatus: string | null;
  resolutionNotes: string | null;
  laborBucket: string;
  partNumber: string;
}

export interface TimeEntriesResponse {
  data: TimeEntryRecord[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export async function fetchMyTimeEntries(params?: {
  limit?: number;
  offset?: number;
  from?: string;
  to?: string;
}): Promise<TimeEntriesResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  if (params?.from) searchParams.set('from', params.from);
  if (params?.to) searchParams.set('to', params.to);
  const qs = searchParams.toString();
  return apiRequest<TimeEntriesResponse>(`/me/time-entries${qs ? `?${qs}` : ''}`);
}

// --- Profile ---

export interface ProfileResponse {
  data: {
    id: string;
    email: string;
    name: string;
    role: string;
    isActive: boolean;
    targetCapacityPercent: number;
    skills: Array<{ id: string; name: string }>;
    createdAt: string | null;
    updatedAt: string | null;
  };
}

export async function fetchMyProfile(): Promise<ProfileResponse> {
  return apiRequest<ProfileResponse>('/me');
}

// --- WebSocket Ticket ---

export interface WsTicketResponse {
  data: {
    ticket: string;
    expiresAt: string;
  };
}

/**
 * Request a single-use WebSocket ticket.
 * Ticket is valid for 30 seconds and can only be used once.
 */
export async function fetchWsTicket(): Promise<WsTicketResponse> {
  return apiRequest<WsTicketResponse>('/ws-ticket', {
    method: 'POST',
  });
}

/**
 * Get the WebSocket URL for connecting.
 */
export function getWebSocketUrl(ticket: string): string {
  // Use page protocol to determine ws/wss (matches planner implementation)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  // Extract host from API_BASE_URL using proper URL parsing
  let host: string;
  if (import.meta.env.DEV) {
    host = 'localhost:8787';
  } else if (API_BASE_URL) {
    try {
      host = new URL(API_BASE_URL).host;
    } catch (err) {
      console.error('Invalid API_BASE_URL for WebSocket:', API_BASE_URL, err);
      host = window.location.host;
    }
  } else {
    console.warn('API_BASE_URL not configured; WebSocket using window.location.host as fallback');
    host = window.location.host;
  }

  return `${protocol}//${host}/ws?ticket=${encodeURIComponent(ticket)}`;
}

/**
 * Get human-readable labor bucket label.
 */
export function getLaborBucketLabel(bucket: string): string {
  const labels: Record<string, string> = {
    mechanical_engineering: 'Mechanical Eng',
    electrical_engineering: 'Electrical Eng',
    software_design: 'Software Design',
    image_formation_design: 'Image Formation',
    software_development: 'Software Dev',
    fabrication: 'Fabrication',
    electrical_assembly: 'Electrical Assembly',
    final_assembly: 'Final Assembly',
    testing_qc: 'Testing/QC',
    packing_shipping: 'Pack & Ship',
    procuring: 'Procurement',
    waiting_for_parts: 'Awaiting Parts',
    stage_parts: 'Stage Parts',
    supply_chain_release: 'Supply Chain',
    awaiting_confirmation: 'Awaiting Confirmation',
    awaiting_shipment: 'Awaiting Shipment',
    awaiting_receipt: 'Awaiting Receipt',
  };
  return labels[bucket] ?? bucket;
}
